import * as React from "react"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Check, ChevronDown } from "lucide-react"

interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  placeholder?: string
}

interface SelectContextValue {
  value: string
  onValueChange: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
}

const SelectContext = React.createContext<SelectContextValue | undefined>(undefined)

export function Select({ value = "", onValueChange, children }: SelectProps) {
  const [open, setOpen] = React.useState(false)

  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue)
    setOpen(false)
  }

  return (
    <SelectContext.Provider value={{ value, onValueChange: handleValueChange, open, setOpen }}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </SelectContext.Provider>
  )
}

export function SelectTrigger({ 
  children, 
  className = "",
  placeholder = "Select..."
}: { 
  children?: React.ReactNode
  className?: string
  placeholder?: string
}) {
  const context = React.useContext(SelectContext)
  if (!context) throw new Error("SelectTrigger must be used within Select")

  return (
    <PopoverTrigger asChild>
      <button
        type="button"
        className={`flex w-full min-w-0 items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500 disabled:cursor-not-allowed disabled:opacity-50 transition-colors h-8 ${className}`}
      >
        <span className={context.value ? "text-foreground truncate" : "text-muted-foreground truncate"}>
          {children || placeholder}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
      </button>
    </PopoverTrigger>
  )
}

export function SelectValue({ placeholder = "Select..." }: { placeholder?: string }) {
  const context = React.useContext(SelectContext)
  if (!context) throw new Error("SelectValue must be used within Select")

  return <>{context.value || placeholder}</>
}

export function SelectContent({ 
  children,
  className = "" 
}: { 
  children: React.ReactNode
  className?: string
}) {
  return (
    <PopoverContent className={`w-full max-h-60 overflow-auto bg-background border border-border shadow-lg ${className}`} align="start">
      <div className="p-1">
        {children}
      </div>
    </PopoverContent>
  )
}

export function SelectItem({ 
  value, 
  children,
  className = "" 
}: { 
  value: string
  children: React.ReactNode
  className?: string
}) {
  const context = React.useContext(SelectContext)
  if (!context) throw new Error("SelectItem must be used within Select")

  const isSelected = context.value === value

  return (
    <button
      type="button"
      onClick={() => context.onValueChange(value)}
      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-muted focus:bg-muted ${
        isSelected ? "bg-muted" : ""
      } ${className}`}
    >
      <span className="flex-1 text-left">{children}</span>
      {isSelected && (
        <Check className="h-4 w-4 text-orange-500" />
      )}
    </button>
  )
}
